import type { ToastInput, ToastRecord, ToastStoreSnapshot } from "./toast.types";

const MAX_VISIBLE_TOASTS = 3;
const DEFAULT_DURATION_BY_VARIANT = {
  success: 3_500,
  info: 4_500,
  warning: 6_000,
  error: 8_000,
} as const;

let nextToastId = 0;
let snapshot: ToastStoreSnapshot = { toasts: [] };
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function updateToasts(updater: (toasts: ToastRecord[]) => ToastRecord[]): void {
  snapshot = { toasts: updater(snapshot.toasts) };
  emit();
}

function resolveToast(input: ToastInput): ToastRecord {
  const variant = input.variant ?? "info";
  return {
    ...input,
    id: `toast-${(nextToastId += 1)}`,
    variant,
    durationMs: input.durationMs ?? DEFAULT_DURATION_BY_VARIANT[variant],
    createdAt: Date.now(),
  };
}

function isStickyToast(toast: ToastRecord): boolean {
  return toast.durationMs <= 0;
}

function limitVisibleToasts(toasts: ToastRecord[]): ToastRecord[] {
  const sticky = toasts.filter(isStickyToast);
  const transient = toasts.filter((toast) => !isStickyToast(toast));
  const transientSlots = Math.max(MAX_VISIBLE_TOASTS - sticky.length, 0);

  return [...sticky, ...transient.slice(0, transientSlots)].sort(
    (a, b) => b.createdAt - a.createdAt
  );
}

export function subscribeToToastStore(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getToastSnapshot(): ToastStoreSnapshot {
  return snapshot;
}

export function pushToast(input: ToastInput): string {
  // Dedupe by updating the existing toast IN PLACE, preserving its id and createdAt.
  // The previous approach removed the old toast and inserted a new one with a fresh id,
  // which remounted the DOM node (re-announcing role="alert") and re-armed a full-duration
  // timer — so a sustained error burst kept one toast alive and blinking forever. Keeping
  // the id/createdAt refreshes the content without remounting or re-arming the timer (P-198).
  if (input.dedupeKey) {
    const existing = snapshot.toasts.find((toast) => toast.dedupeKey === input.dedupeKey);
    if (existing) {
      const variant = input.variant ?? existing.variant;
      const updated: ToastRecord = {
        ...existing,
        ...input,
        id: existing.id,
        variant,
        durationMs: input.durationMs ?? DEFAULT_DURATION_BY_VARIANT[variant],
        createdAt: existing.createdAt,
      };
      updateToasts((current) =>
        limitVisibleToasts(current.map((toast) => (toast.id === existing.id ? updated : toast)))
      );
      return existing.id;
    }
  }

  const nextToast = resolveToast(input);
  updateToasts((current) => limitVisibleToasts([nextToast, ...current]));
  return nextToast.id;
}

export function dismissToast(id: string): void {
  updateToasts((current) => current.filter((toast) => toast.id !== id));
}

export function clearToasts(): void {
  updateToasts(() => []);
}

type ToastApi = ((input: ToastInput) => string) & {
  success: (input: Omit<ToastInput, "variant">) => string;
  info: (input: Omit<ToastInput, "variant">) => string;
  warning: (input: Omit<ToastInput, "variant">) => string;
  error: (input: Omit<ToastInput, "variant">) => string;
  dismiss: (id: string) => void;
  clear: () => void;
  getState: () => ToastStoreSnapshot;
};

const makeVariantPusher =
  (variant: ToastRecord["variant"]) => (input: Omit<ToastInput, "variant">) =>
    pushToast({ ...input, variant });

export const toast: ToastApi = Object.assign((input: ToastInput) => pushToast(input), {
  success: makeVariantPusher("success"),
  info: makeVariantPusher("info"),
  warning: makeVariantPusher("warning"),
  error: makeVariantPusher("error"),
  dismiss: dismissToast,
  clear: clearToasts,
  getState: getToastSnapshot,
});

export { MAX_VISIBLE_TOASTS };
